import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useCart } from '../contexts/CartContext';
import Footer from '../components/Footer';
import { ShoppingBag, ArrowLeft, CheckCircle, CreditCard, Building2 } from 'lucide-react';
import { PaymentBadgesDetailed } from '../components/PaymentBadges';
import { trackMetaEvent, formatCartData } from '../utils/metaPixel';

const Checkout = () => {
  const { cart, getCartTotal, getOrderTotal, DELIVERY_FEE, clearCart } = useCart();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [payhereLoaded, setPayhereLoaded] = useState(false);

  // Load PayHere script
  useEffect(() => {
    // Check if script is already loaded
    if (document.getElementById('payhere-script')) {
      setPayhereLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.id = 'payhere-script';
    script.src = 'https://www.payhere.lk/lib/payhere.js';
    script.async = true;
    script.onload = () => setPayhereLoaded(true);
    document.body.appendChild(script);

    return () => {
      // Cleanup if needed
      const existingScript = document.getElementById('payhere-script');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePayHere = () => {
    if (!window.payhere || !payhereLoaded) {
      alert('Payment system is loading. Please try again in a moment.');
      return;
    }

    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'merchm-store';
    const merchantId = process.env.REACT_APP_PAYHERE_MERCHANT_ID || '239283';

    const payment = {
      sandbox: true, // Set to false for production
      merchant_id: merchantId,
      return_url: `${window.location.origin}/thankyou?order_id=${orderId}`,
      cancel_url: `${window.location.origin}/checkout`,
      notify_url: `https://us-central1-${projectId}.cloudfunctions.net/payhereNotify`,
      order_id: orderId,
      items: cart.map(item => item.name).join(', '),
      amount: getOrderTotal().toFixed(2),
      currency: 'LKR',
      first_name: formData.fullName.split(' ')[0] || formData.fullName,
      last_name: formData.fullName.split(' ').slice(1).join(' ') || '',
      email: formData.email || 'customer@example.com',
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      country: 'Sri Lanka',
    };

    window.payhere.onCompleted = function onCompleted(orderId) {
      console.log('Payment completed. OrderID:', orderId);
      navigate(`/thankyou?order_id=${orderId}`);
    };

    window.payhere.onDismissed = function onDismissed() {
      console.log('Payment dismissed');
    };

    window.payhere.onError = function onError(error) {
      console.log('Error:', error);
      alert('Payment failed. Please try again or choose bank transfer.');
    };

    window.payhere.startPayment(payment);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Track InitiateCheckout event BEFORE creating order
      trackMetaEvent('InitiateCheckout', formatCartData(cart));

      // Create order in Firestore
      const orderData = {
        customer: {
          fullName: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
        },
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          source: item.source,
          category: item.category || '',
          imgPath: item.imgPath || '',
        })),
        pricing: {
          subtotal: getCartTotal(),
          delivery: DELIVERY_FEE,
          total: getOrderTotal(),
        },
        notes: formData.notes,
        status: 'pending_payment',
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(firestore, 'Orders'), orderData);
      setOrderId(docRef.id);
      setOrderSuccess(true);
      clearCart();
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to submit order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cart.length === 0 && !orderSuccess) {
    return (
      <main className="bg-surface min-h-screen">
        <div className="max-w-screen-lg mx-auto px-6 py-16 text-center">
          <ShoppingBag className="h-16 w-16 text-charcoal/20 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">Your cart is empty</h1>
          <p className="text-ink-2 mb-6">Add some products before checking out!</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full hover:bg-primary/90 transition"
          >
            <ArrowLeft className="h-5 w-5" />
            Continue Shopping
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  if (orderSuccess) {
    return (
      <main className="bg-surface min-h-screen">
        <div className="max-w-screen-lg mx-auto px-6 py-16">
          <div className="bg-white rounded-3xl border border-charcoal/10 p-8 md:p-12">
            <div className="text-center mb-8">
              <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-6 animate-zoomIn" />
              <h1 className="text-3xl font-bold mb-4">Order Placed Successfully!</h1>
              <p className="text-ink-2 mb-2">Order ID: <span className="font-mono font-semibold text-primary">{orderId}</span></p>
              <p className="text-ink-2">We've received your order. Please complete the payment to proceed.</p>
            </div>

            {/* Payment Amount Display */}
            <div className="bg-surface rounded-2xl p-6 mb-8 text-center max-w-md mx-auto">
              <p className="text-ink-3 mb-2">Total Amount</p>
              <p className="text-4xl font-bold text-primary">Rs. {getOrderTotal().toLocaleString('en-LK')}</p>
            </div>

            {/* Payment Options */}
            <div className="max-w-2xl mx-auto mb-8">
              <h2 className="text-xl font-bold mb-6 text-center">Choose Payment Method</h2>

              <div className="grid md:grid-cols-2 gap-6">
                {/* PayHere Online Payment */}
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/20 rounded-2xl p-6 hover:border-primary/40 transition">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center">
                      <CreditCard className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Pay Online Now</h3>
                      <p className="text-sm text-ink-3">Instant confirmation</p>
                    </div>
                  </div>
                  <p className="text-sm text-ink-2 mb-4">
                    Pay securely with credit/debit card or online banking via PayHere
                  </p>
                  <button
                    onClick={handlePayHere}
                    disabled={!payhereLoaded}
                    className="w-full py-3 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {payhereLoaded ? 'Pay Rs. 4,890 Now' : 'Loading Payment...'}
                  </button>
                  <p className="text-xs text-ink-3 mt-3 text-center">
                    Secure payment powered by PayHere
                  </p>
                </div>

                {/* Bank Transfer Option */}
                <div className="bg-surface border-2 border-charcoal/10 rounded-2xl p-6 hover:border-charcoal/20 transition">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 rounded-full bg-charcoal flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Pay Later</h3>
                      <p className="text-sm text-ink-3">Bank transfer</p>
                    </div>
                  </div>
                  <p className="text-sm text-ink-2 mb-4">
                    We'll contact you on WhatsApp with bank details and payment instructions
                  </p>
                  <a
                    href="https://wa.me/94771234567"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 bg-emerald-500 text-white rounded-full font-semibold hover:bg-emerald-600 transition text-center"
                  >
                    Contact on WhatsApp
                  </a>
                  <p className="text-xs text-ink-3 mt-3 text-center">
                    We'll respond within 24 hours
                  </p>
                </div>
              </div>
            </div>

            {/* Bank Transfer Details (Collapsed) */}
            <div className="max-w-2xl mx-auto mb-8">
              <details className="bg-surface rounded-2xl border border-charcoal/10 overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold hover:bg-charcoal/5 transition">
                  View Bank Transfer Details
                </summary>
                <div className="p-6 border-t border-charcoal/10">
                  <div className="space-y-3 text-sm mb-6">
                    <div>
                      <p className="text-ink-3 mb-1">Bank Name:</p>
                      <p className="font-semibold">Commercial Bank of Ceylon</p>
                    </div>
                    <div>
                      <p className="text-ink-3 mb-1">Account Name:</p>
                      <p className="font-semibold">Merch Masters LK</p>
                    </div>
                    <div>
                      <p className="text-ink-3 mb-1">Account Number:</p>
                      <p className="font-semibold font-mono">1234567890</p>
                    </div>
                    <div>
                      <p className="text-ink-3 mb-1">Branch:</p>
                      <p className="font-semibold">Colombo</p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h4 className="font-semibold mb-2 text-amber-900 text-sm">Instructions:</h4>
                    <ul className="text-xs text-amber-800 space-y-1">
                      <li>• Transfer Rs. {getOrderTotal().toLocaleString('en-LK')}</li>
                      <li>• Use Order ID: <span className="font-mono font-semibold">{orderId}</span> as reference</li>
                      <li>• Send receipt to WhatsApp: +94 77 123 4567</li>
                      <li>• Payment confirmation within 24 hours</li>
                    </ul>
                  </div>
                </div>
              </details>
            </div>

            {/* Back Button */}
            <div className="text-center">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-6 py-3 border border-charcoal/20 rounded-full hover:bg-surface transition"
              >
                <ArrowLeft className="h-5 w-5" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="bg-surface min-h-screen">
      <div className="max-w-screen-lg mx-auto px-6 py-16">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition mb-6"
        >
          <ArrowLeft className="h-5 w-5" />
          Back
        </button>

        <h1 className="text-3xl font-bold mb-8">Checkout</h1>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Form */}
          <div className="bg-white rounded-3xl border border-charcoal/10 p-8">
            <h2 className="text-xl font-bold mb-6">Delivery Information</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="fullName" className="block text-sm font-semibold mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-charcoal/15 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-charcoal/15 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="+94 77 123 4567"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-charcoal/15 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-semibold mb-2">
                  Delivery Address *
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-4 py-3 rounded-xl border border-charcoal/15 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="123 Main Street, Apartment 4B"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-semibold mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-charcoal/15 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="Colombo"
                  />
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-semibold mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-charcoal/15 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="10000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-semibold mb-2">
                  Order Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 rounded-xl border border-charcoal/15 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="Any special instructions for your order..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Placing Order...' : 'Place Order'}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-charcoal/10 p-8">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={`${item.id}-${item.source}`} className="flex gap-4">
                    <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-surface">
                      {item.imgPath ? (
                        <img
                          src={item.imgPath}
                          alt={item.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-charcoal/30">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold">{item.name}</h3>
                      <p className="text-xs text-ink-3">Qty: {item.quantity}</p>
                      <p className="text-sm font-bold text-primary mt-1">
                        Rs. {(item.price * item.quantity).toLocaleString('en-LK')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-charcoal/10 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-ink-2">Subtotal</span>
                  <span className="font-semibold">Rs. {getCartTotal().toLocaleString('en-LK')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ink-2">Delivery</span>
                  <span className="font-semibold">Rs. {DELIVERY_FEE.toLocaleString('en-LK')}</span>
                </div>
                <div className="flex justify-between border-t border-charcoal/10 pt-2">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-lg font-bold text-primary">
                    Rs. {getOrderTotal().toLocaleString('en-LK')}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-2xl p-6 border border-charcoal/10">
              <PaymentBadgesDetailed variant="light" />
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 mt-4">
                <p className="text-xs text-blue-900 font-semibold mb-1">Free Island-wide Delivery</p>
                <p className="text-xs text-blue-700">Rs. 400 delivery fee included in your total</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default Checkout;
